import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, clinicApi, clinicApiFor } from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';
import type {
  ActiveClinic,
  CreateClinicPayload,
  CreateClinicResponse,
  CreateInvitationPayload,
  CreatedInvitation,
  Invitation,
  PendingInvitationForUser,
} from '@/types/api';

// Exported because role mutations must invalidate it too — it is the only
// source of the caller's own permissions. See `useRoleMutation`.
export const MEMBERSHIPS_KEY = ['clinics', 'me'] as const;

/** Add or refresh one membership, leaving the rest of the list alone. */
function upsertMembership(list: ActiveClinic[], next: ActiveClinic): ActiveClinic[] {
  const without = list.filter((m) => m.id !== next.id);
  return [...without, next];
}

interface UseMyMembershipsOptions {
  /** Skip the request entirely (e.g. when not authenticated). */
  enabled?: boolean;
}

export function useMyMemberships(options: UseMyMembershipsOptions = {}) {
  const setMemberships = useAuthStore((s) => s.setMemberships);
  return useQuery({
    queryKey: MEMBERSHIPS_KEY,
    queryFn: async () => {
      const memberships = await api.get<ActiveClinic[]>('/api/clinics/me');
      // Keep the auth store in sync so RequireAuth/RequirePermission and the
      // post-auth router see fresh memberships after onboarding.
      setMemberships(memberships);
      return memberships;
    },
    enabled: options.enabled ?? true,
  });
}

/** A clinic's public identity — everything an unauthenticated page may see. */
export interface PublicClinicRef {
  id: string;
  name: string;
  slug: string;
}

/**
 * Look up a clinic by slug OR by id, without a session.
 *
 * The id form is what lets a QR code printed before slugs existed find its way
 * to the clinic's current URL — see `LegacyPublicRedirect`.
 */
export function usePublicClinicRef(ref: string | undefined) {
  return useQuery({
    queryKey: ['public-clinic-ref', ref],
    queryFn: () =>
      api.get<PublicClinicRef>(`/api/public/clinics/${encodeURIComponent(ref as string)}`, {
        auth: false,
      }),
    enabled: !!ref,
    // A bad link is bad on every attempt; retrying just delays the error state
    // the visitor needs to see.
    retry: false,
    staleTime: 5 * 60_000,
  });
}

export function useCreateClinic() {
  const qc = useQueryClient();
  const addMembership = useAuthStore((s) => s.addMembership);
  return useMutation({
    mutationFn: (payload: CreateClinicPayload) =>
      api.post<CreateClinicResponse>('/api/clinics', payload),
    onSuccess: (result) => {
      // Merged, not replaced: the creator may already belong to other clinics,
      // and overwriting the list would make RequireAuth decide those are not
      // theirs and evict any open tab sitting in one.
      //
      // No token swap. One used to be mandatory here because the caller's token
      // named their clinic and its permissions; a token names only the user now,
      // so the one they arrived with already works inside the new clinic.
      addMembership(result.activeClinic);
      qc.setQueryData<ActiveClinic[]>(MEMBERSHIPS_KEY, (prev) =>
        upsertMembership(prev ?? [], result.activeClinic),
      );
    },
  });
}

// No clinic id in the key: the whole page is inside one clinic's namespace, and
// a hard navigation (the only way to reach another) discards the cache anyway.
const INVITATIONS_KEY = ['invitations'] as const;

/**
 * Reading invitations is its own permission (`invitations.read`), and the pages
 * that show them are gated on something broader — so callers must not mount this
 * hook unconditionally, or a member holding only `members.read` collects a
 * guaranteed 403 on page load. `PeoplePage` renders the whole section behind the
 * permission, which is what keeps the query unmounted.
 */
export function useListInvitations() {
  return useQuery({
    queryKey: INVITATIONS_KEY,
    queryFn: () => clinicApi.get<Invitation[]>('invitations'),
  });
}

/**
 * `clinicSlug` overrides which clinic is invited into. Only onboarding passes
 * it: it invites into the clinic it has just created while the page is still in
 * the global namespace, where there is no slug in the URL to read.
 */
export function useCreateInvitation(clinicSlug?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateInvitationPayload) =>
      (clinicSlug ? clinicApiFor(clinicSlug) : clinicApi).post<CreatedInvitation>(
        'invitations',
        payload,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: INVITATIONS_KEY });
    },
  });
}

const MY_PENDING_INVITES_KEY = ['auth', 'me', 'pending-invitations'] as const;

/** Pending invitations addressed to the current user's email, across clinics. */
export function useMyPendingInvitations(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: MY_PENDING_INVITES_KEY,
    queryFn: () =>
      api.get<PendingInvitationForUser[]>('/api/auth/me/pending-invitations'),
    enabled: options.enabled ?? true,
  });
}

/**
 * Self-service accept of one of the caller's pending invitations. The granted
 * membership is merged into the auth store — never replacing the list — so a
 * user who already works at two clinics does not lose them by joining a third.
 */
export function useAcceptMyInvitation() {
  const qc = useQueryClient();
  const addMembership = useAuthStore((s) => s.addMembership);
  return useMutation({
    mutationFn: (invitationId: string) =>
      api.post<ActiveClinic>(`/api/auth/me/pending-invitations/${invitationId}/accept`),
    onSuccess: (membership) => {
      addMembership(membership);
      qc.setQueryData<ActiveClinic[]>(MEMBERSHIPS_KEY, (prev) =>
        upsertMembership(prev ?? [], membership),
      );
      qc.invalidateQueries({ queryKey: MY_PENDING_INVITES_KEY });
    },
  });
}

export function useRevokeInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) =>
      clinicApi.delete<{ revoked: boolean }>(`invitations/${invitationId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: INVITATIONS_KEY });
    },
  });
}
